/**
 * The UI test arms this browser gets, and the one exposure row per visit per
 * test. Components read it through `UiTest` / `useUiTestArm`
 * (components/ui-tests/UiTest.tsx), never directly.
 *
 * The arms come in the `sw_ui` cookie, which proxy.ts sets on every page
 * response from the visitor's id and the running tests (utils/ui-tests/
 * cookie.ts). Read after hydration, with no request and nothing to wait for:
 * owner, 2026-10-25, "the client can't be slower bc of ui a/b testing".
 *
 * `?ui_test=thumb-location:location` forces an arm in this tab (kept in
 * sessionStorage across navigation, `?ui_test=off` clears it). A forced arm is
 * never recorded. Without a provider (unit tests, isolated renders) everything
 * is control and nothing is recorded.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { UI_TEST_EXPOSURE_EVENT, type ServedUiTest } from '../schemas/ui-tests';
import { getAnalytics } from '../utils/analytics';
import { UI_ARMS_COOKIE, decodeArms, readCookie } from '../utils/ui-tests/cookie';

const FORCE_KEY = 'sw_ui_test_force';
const EXPOSED_PREFIX = 'sw_ui_exposed:';

export interface UiTestContextValue {
  /** What each test serves, by test id; null until the cookie has been read (after hydration). */
  served: Record<string, ServedUiTest> | null;
  /** True once the cookie has been read. Only then is anything recorded. */
  settled: boolean;
  /** Arms forced with `?ui_test=`, by test id. */
  forced: Record<string, string>;
  /** One exposure row per visit per test run and arm. A no-op when it must not count. */
  recordExposure: (test: string) => void;
}

const NOTHING: UiTestContextValue = {
  served: {},
  settled: true,
  forced: {},
  recordExposure: () => {},
};

const UiTestContext = createContext<UiTestContextValue>(NOTHING);

export const useUiTests = (): UiTestContextValue => useContext(UiTestContext);

/** `thumb-location:location,home-default-view:control` into a map; `off` is empty. */
export function parseForcedArms(param: string | null | undefined): Record<string, string> | null {
  if (param == null) return null;
  if (param === 'off' || param === '') return {};
  const forced: Record<string, string> = {};
  for (const pair of param.split(',')) {
    const [test, arm] = pair.split(':').map((s) => s.trim());
    if (test && arm && /^[a-z0-9-]+$/.test(test) && /^[a-z0-9-]+$/.test(arm)) forced[test] = arm;
  }
  return forced;
}

function readJson(storage: Storage | undefined, key: string): unknown {
  try {
    const raw = storage?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  // eslint-disable-next-line sellwild-code/catch-must-log -- blocked storage is a reader's setting, not a fault; the arms still come from the server
  } catch {
    return null;
  }
}

function writeJson(storage: Storage | undefined, key: string, value: unknown): void {
  try {
    storage?.setItem(key, JSON.stringify(value));
  // eslint-disable-next-line sellwild-code/catch-must-log -- blocked storage only costs the first-paint cache
  } catch {
    // Nothing to do: the next load fetches again.
  }
}

/**
 * The tab's sessionStorage, or undefined. The property itself throws when the
 * browser blocks storage ("block all cookies", a sandboxed iframe), and this
 * runs in the provider's mount effect, above every error boundary but the
 * page's: one throw here is an error page on every page.
 */
const session = (): Storage | undefined => {
  try {
    return window.sessionStorage;
  // eslint-disable-next-line sellwild-code/catch-must-log -- blocked storage is a reader's setting, not a fault
  } catch {
    return undefined;
  }
};

/** The page's cookies, or '' where reading them throws (a sandboxed iframe): everyone gets control. */
const pageCookies = (): string => {
  try {
    return document.cookie;
  // eslint-disable-next-line sellwild-code/catch-must-log -- a sandboxed frame is the embedder's setting, not a fault
  } catch {
    return '';
  }
};

export const UiTestProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [served, setServed] = useState<Record<string, ServedUiTest> | null>(null);
  const [settled, setSettled] = useState(false);
  const [forced, setForced] = useState<Record<string, string>>({});
  // What the server served this browser, which is the only thing exposures are written from.
  const fresh = useRef<Record<string, ServedUiTest> | null>(null);
  // In-memory too, for a browser whose sessionStorage throws.
  const recorded = useRef(new Set<string>());

  useEffect(() => {
    // After hydration: the server's HTML is the same for everyone (control),
    // and reading the cookie during render would not match it.
    const tests = decodeArms(readCookie(pageCookies(), UI_ARMS_COOKIE));
    fresh.current = tests;

    const fromUrl = parseForcedArms(new URLSearchParams(window.location.search).get('ui_test'));
    if (fromUrl) writeJson(session(), FORCE_KEY, fromUrl);
    const stored = readJson(session(), FORCE_KEY);

    // eslint-disable-next-line react-hooks/set-state-in-effect -- the cookie and storage are read after hydration; reading them during render would mismatch the server's HTML
    if (stored && typeof stored === 'object') setForced(stored as Record<string, string>);
    setServed(tests);
    setSettled(true);
  }, []);

  const recordExposure = useCallback(
    (test: string) => {
      const answer = fresh.current?.[test];
      if (!answer?.record || forced[test]) return;
      const key = `${EXPOSED_PREFIX}${answer.run}:${answer.arm}`;
      if (recorded.current.has(key)) return;
      recorded.current.add(key);
      try {
        if (session()?.getItem(key)) return;
        session()?.setItem(key, '1');
      // eslint-disable-next-line sellwild-code/catch-must-log -- blocked storage: the in-memory set above still holds this page to one row
      } catch {
        // Fall through and record once for this page.
      }
      getAnalytics()?.track({
        event_type: UI_TEST_EXPOSURE_EVENT,
        properties: { test, run: answer.run, arm: answer.arm, phase: answer.phase },
      });
    },
    [forced]
  );

  const value = useMemo(() => ({ served, settled, forced, recordExposure }), [served, settled, forced, recordExposure]);
  return <UiTestContext.Provider value={value}>{children}</UiTestContext.Provider>;
};
